import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Description, Fieldset, Label, Switch } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductListDrawerFilters } from '../productListFilters';

type StockPatch = Pick<
    ProductListDrawerFilters,
    'lowStock' | 'includeArchived' | 'draft'
>;

type StockFilterFieldsProps = StockPatch & {
    onChange: (patch: Partial<StockPatch>) => void;
};

/**
 * The filters drawer's Stock group: Low stock only, Show archived and Drafts
 * only (bos-app#226), the switches that narrow the list by a product's state.
 */
export const StockFilterFields: React.FC<StockFilterFieldsProps> = ({
    lowStock,
    includeArchived,
    draft,
    onChange,
}) => {
    const { t } = useTranslation('pages');

    const switches = [
        {
            testId: 'product-filters-low-stock',
            isSelected: lowStock,
            toPatch: (next: boolean) => ({ lowStock: next }),
            label: t('products.components.productTable.filters.lowStockLabel'),
            description: t(
                'products.components.productTable.filters.lowStockDescription',
            ),
        },
        {
            testId: 'product-filters-archived',
            isSelected: includeArchived,
            toPatch: (next: boolean) => ({ includeArchived: next }),
            label: t(
                'products.components.productTable.filters.showArchivedLabel',
            ),
            description: t(
                'products.components.productTable.filters.showArchivedDescription',
            ),
        },
        {
            testId: 'product-filters-drafts',
            isSelected: draft,
            toPatch: (next: boolean) => ({ draft: next }),
            label: t('products.components.productTable.filters.draftsLabel'),
            description: t(
                'products.components.productTable.filters.draftsDescription',
            ),
        },
    ];

    return (
        <Fieldset className="flex-none gap-0">
            <Fieldset.Legend className="mb-2">
                <Label elementType="span">
                    {t('products.components.productTable.filters.stockLegend')}
                </Label>
            </Fieldset.Legend>
            <Fieldset.Group>
                {switches.map((item) => (
                    <Switch
                        isSelected={item.isSelected}
                        key={item.testId}
                        onChange={(next) => onChange(item.toPatch(next))}
                        {...getNodeTestIdProp(item.testId)}
                    >
                        <Switch.Content>
                            <Switch.Control>
                                <Switch.Thumb />
                            </Switch.Control>
                            {item.label}
                        </Switch.Content>
                        <Description>{item.description}</Description>
                    </Switch>
                ))}
            </Fieldset.Group>
        </Fieldset>
    );
};
